import { useState, useEffect } from 'react';
import { Check, X, Clock, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { apiGet, apiPost } from '../../services/api';
import toast from 'react-hot-toast';
import useAuthStore from '../../context/authStore';

const statusConfig = {
  draft: { color: 'bg-gray-100', textColor: 'text-gray-700', label: 'Draft', icon: '📝' },
  pending_client_approval: { color: 'bg-yellow-100', textColor: 'text-yellow-700', label: 'Awaiting Approval', icon: '⏳' },
  approved_by_client: { color: 'bg-green-100', textColor: 'text-green-700', label: 'Approved', icon: '✓' },
  rejected: { color: 'bg-red-100', textColor: 'text-red-700', label: 'Rejected', icon: '✗' },
};

export default function TimesheetApprovalsPage() {
  const { user } = useAuthStore();
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState(null); // 'approve', 'adjust', 'reject'
  const [feedback, setFeedback] = useState('');
  const [workerRating, setWorkerRating] = useState({
    ratingOverall: 0,
    ratingAttendance: 0,
    ratingProfessionalism: 0,
    ratingClinicalFit: 0,
    comments: '',
  });
  const [adjustedHours, setAdjustedHours] = useState({ regular: 0, overtime: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [expandedTimesheetId, setExpandedTimesheetId] = useState(null);
  const [filter, setFilter] = useState('pending_client_approval');

  useEffect(() => {
    if (user?.organizationId) {
      fetchTimesheets();
    }
  }, [user?.organizationId, filter]);

  const fetchTimesheets = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`/staffing/timesheets?status=${filter}`);
      setTimesheets(response.timesheets || []);
    } catch (error) {
      toast.error('Failed to load timesheets');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (timesheet, actionType) => {
    setSelectedTimesheet(timesheet);
    setAction(actionType);
    setFeedback('');
    setAdjustedHours({ 
      regular: timesheet.totals?.regularHours || 0, 
      overtime: timesheet.totals?.overtimeHours || 0 
    });
    const existingRating = timesheet.workerRatingSummary || timesheet.clientApproval?.workerRating || {};
    setWorkerRating({
      ratingOverall: Number(existingRating.ratingOverall || 0),
      ratingAttendance: Number(existingRating.ratingAttendance || 0),
      ratingProfessionalism: Number(existingRating.ratingProfessionalism || 0),
      ratingClinicalFit: Number(existingRating.ratingClinicalFit || 0),
      comments: existingRating.comments || '',
    });
    setShowModal(true);
  };

  const handleApprove = async () => {
    if (!selectedTimesheet) return;
    if (!workerRating.ratingOverall || workerRating.ratingOverall < 1 || workerRating.ratingOverall > 5) {
      toast.error('Overall worker rating (1-5) is required');
      return;
    }

    try {
      setSubmitting(true);
      const response = await apiPost(
        `/staffing/timesheets/${selectedTimesheet._id}/client-approval`,
        {
          status: 'approved',
          notes: feedback,
          workerRating: {
            ratingOverall: Number(workerRating.ratingOverall),
            ratingAttendance: Number(workerRating.ratingAttendance || 0) || undefined,
            ratingProfessionalism: Number(workerRating.ratingProfessionalism || 0) || undefined,
            ratingClinicalFit: Number(workerRating.ratingClinicalFit || 0) || undefined,
            comments: workerRating.comments,
          },
        }
      );

      toast.success('Timesheet approved');
      setShowModal(false);
      setFeedback('');
      fetchTimesheets();
    } catch (error) {
      toast.error(error.message || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdjust = async () => {
    if (!selectedTimesheet) return;

    if (!feedback.trim()) {
      toast.error('Please provide a reason for adjustment');
      return;
    }

    if (adjustedHours.regular < 0 || adjustedHours.overtime < 0) {
      toast.error('Hours cannot be negative');
      return;
    }
    if (!workerRating.ratingOverall || workerRating.ratingOverall < 1 || workerRating.ratingOverall > 5) {
      toast.error('Overall worker rating (1-5) is required');
      return;
    }

    try {
      setSubmitting(true);
      const response = await apiPost(
        `/staffing/timesheets/${selectedTimesheet._id}/client-approval`,
        {
          status: 'adjusted',
          adjustedRegularHours: parseFloat(adjustedHours.regular),
          adjustedOvertimeHours: parseFloat(adjustedHours.overtime),
          reason: feedback,
          notes: `Adjusted by facility manager`,
          workerRating: {
            ratingOverall: Number(workerRating.ratingOverall),
            ratingAttendance: Number(workerRating.ratingAttendance || 0) || undefined,
            ratingProfessionalism: Number(workerRating.ratingProfessionalism || 0) || undefined,
            ratingClinicalFit: Number(workerRating.ratingClinicalFit || 0) || undefined,
            comments: workerRating.comments,
          },
        }
      );

      toast.success('Timesheet adjusted and approved');
      setShowModal(false);
      setFeedback('');
      fetchTimesheets();
    } catch (error) {
      toast.error(error.message || 'Failed to adjust');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selectedTimesheet) return;

    if (!feedback.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      setSubmitting(true);
      const response = await apiPost(
        `/staffing/timesheets/${selectedTimesheet._id}/client-approval`,
        {
          status: 'rejected',
          notes: feedback,
        }
      );

      toast.success('Timesheet rejected - worker will be notified');
      setShowModal(false);
      setFeedback('');
      fetchTimesheets();
    } catch (error) {
      toast.error(error.message || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  const calculatePayChange = () => {
    if (!selectedTimesheet) return 0;
    const originalTotal = selectedTimesheet.totals?.regularHours + selectedTimesheet.totals?.overtimeHours;
    const newTotal = parseFloat(adjustedHours.regular) + parseFloat(adjustedHours.overtime);
    const ratio = originalTotal > 0 ? newTotal / originalTotal : 1;
    
    // Assuming pay depends on rates in shifts
    const firstShift = selectedTimesheet.shifts?.[0];
    if (!firstShift) return 0;
    
    const regularPay = adjustedHours.regular * firstShift.payRate;
    const overtimePay = adjustedHours.overtime * firstShift.payRate * 1.5;
    return regularPay + overtimePay;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Timesheet Approvals</h1>
        <p className="text-gray-600 mt-2">Review and approve worker timesheets for your facility</p>
      </div>

      {/* Filter Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-8">
          {['pending_client_approval', 'approved_by_client', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-1 py-3 text-sm font-medium border-b-2 ${
                filter === status
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {status === 'pending_client_approval' && `Awaiting Review (${timesheets.filter(t => t.status === 'pending_client_approval').length})`}
              {status === 'approved_by_client' && `Approved (${timesheets.filter(t => t.status === 'approved_by_client').length})`}
              {status === 'rejected' && `Rejected (${timesheets.filter(t => t.status === 'rejected').length})`}
            </button>
          ))}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="text-gray-600">Loading timesheets...</div>
        </div>
      )}

      {/* Empty State */}
      {!loading && timesheets.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No timesheets to display</p>
        </div>
      )}

      {/* Timesheets List */}
      {!loading && timesheets.length > 0 && (
        <div className="space-y-4">
          {timesheets.map((timesheet) => (
            <div key={timesheet._id} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Timesheet Header */}
              <div className="bg-white p-4 cursor-pointer hover:bg-gray-50" 
                   onClick={() => setExpandedTimesheetId(expandedTimesheetId === timesheet._id ? null : timesheet._id)}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{`${timesheet.workerId?.firstName || ''} ${timesheet.workerId?.lastName || ''}`.trim() || 'Worker'}</h3>
                        <p className="text-sm text-gray-600">
                          {formatDate(timesheet.weekStartDate)} - {formatDate(timesheet.weekEndDate)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{formatCurrency(timesheet.totals?.grossPay || 0)}</div>
                      <div className="text-sm text-gray-600">{timesheet.totals?.totalHours || 0} hrs</div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig[timesheet.status]?.color} ${statusConfig[timesheet.status]?.textColor}`}>
                      {statusConfig[timesheet.status]?.label || timesheet.status}
                    </div>
                    {expandedTimesheetId === timesheet._id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>
              </div>

              {/* Timesheet Details (Expanded) */}
              {expandedTimesheetId === timesheet._id && (
                <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-4">
                  {/* Shifts Table */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Shifts</h4>
                    <div className="overflow-x-auto">
                      <table className="responsive-table w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-300 bg-white">
                            <th className="px-3 py-2 text-left text-gray-700 font-medium">Date</th>
                            <th className="px-3 py-2 text-left text-gray-700 font-medium">Regular Hrs</th>
                            <th className="px-3 py-2 text-left text-gray-700 font-medium">OT Hrs</th>
                            <th className="px-3 py-2 text-left text-gray-700 font-medium">Rate</th>
                            <th className="px-3 py-2 text-right text-gray-700 font-medium">Pay</th>
                          </tr>
                        </thead>
                        <tbody>
                          {timesheet.shifts?.map((shift, idx) => (
                            <tr key={idx} className="border-b border-gray-200 bg-white">
                              <td data-label="Date" className="px-3 py-2">{formatDate(shift.date)}</td>
                              <td data-label="Regular hours" className="px-3 py-2">{shift.regularHours || 0}</td>
                              <td data-label="Overtime" className="px-3 py-2">{shift.overtimeHours || 0}</td>
                              <td data-label="Pay rate" className="px-3 py-2">${shift.payRate?.toFixed(2) || '0.00'}</td>
                              <td data-label="Total pay" className="px-3 py-2 text-right">{formatCurrency(shift.totalPay || 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Totals Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 bg-white p-3 rounded border border-gray-200">
                    <div>
                      <p className="text-xs text-gray-600 uppercase">Regular Hours</p>
                      <p className="text-lg font-semibold text-gray-900">{timesheet.totals?.regularHours || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 uppercase">Overtime Hours</p>
                      <p className="text-lg font-semibold text-gray-900">{timesheet.totals?.overtimeHours || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 uppercase">Total Hours</p>
                      <p className="text-lg font-semibold text-gray-900">{timesheet.totals?.totalHours || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 uppercase">Gross Pay</p>
                      <p className="text-lg font-semibold text-gray-900">{formatCurrency(timesheet.totals?.grossPay || 0)}</p>
                    </div>
                  </div>

                  {/* Worker Notes */}
                  {timesheet.workerNotes && (
                    <div className="bg-white p-3 rounded border border-blue-200">
                      <p className="text-xs text-gray-600 uppercase mb-1">Worker Notes</p>
                      <p className="text-sm text-gray-700">{timesheet.workerNotes}</p>
                    </div>
                  )}

                  {/* Approval Actions */}
                  {timesheet.status === 'pending_client_approval' && (
                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={() => handleOpenModal(timesheet, 'approve')}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2"
                      >
                        <Check className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleOpenModal(timesheet, 'adjust')}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2"
                      >
                        <AlertCircle className="w-4 h-4" />
                        Adjust Hours
                      </button>
                      <button
                        onClick={() => handleOpenModal(timesheet, 'reject')}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center justify-center gap-2"
                      >
                        <X className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  )}

                  {(timesheet.workerRatingSummary || timesheet.clientApproval?.workerRating) && (
                    <div className="bg-white p-3 rounded border border-emerald-200">
                      <p className="text-xs text-gray-600 uppercase mb-1">Worker Rating</p>
                      <p className="text-sm text-gray-700">
                        Overall: <span className="font-semibold">{timesheet.workerRatingSummary?.ratingOverall || timesheet.clientApproval?.workerRating?.ratingOverall || '-'}/5</span>
                      </p>
                      {(timesheet.workerRatingSummary?.comments || timesheet.clientApproval?.workerRating?.comments) && (
                        <p className="text-sm text-gray-700 mt-1">{timesheet.workerRatingSummary?.comments || timesheet.clientApproval?.workerRating?.comments}</p>
                      )}
                    </div>
                  )}

                  {/* Approval Info (if already actioned) */}
                  {timesheet.clientApproval && (
                    <div className="bg-white p-3 rounded border border-gray-200">
                      <p className="text-xs text-gray-600 uppercase mb-2">Approval Details</p>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-600">Status:</span> <span className="font-medium">{timesheet.clientApproval.status}</span></p>
                        <p><span className="text-gray-600">Approved By:</span> <span className="font-medium">{timesheet.clientApproval.approvedBy?.name || 'Admin'}</span></p>
                        <p><span className="text-gray-600">Approved At:</span> <span className="font-medium">{formatDate(timesheet.clientApproval.approvedAt)}</span></p>
                        {timesheet.clientApproval.adjustmentNote && (
                          <p><span className="text-gray-600">Note:</span> <span className="font-medium">{timesheet.clientApproval.adjustmentNote}</span></p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Approval Modal */}
      {showModal && selectedTimesheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {action === 'approve' && 'Approve Timesheet'}
                {action === 'adjust' && 'Adjust Hours & Approve'}
                {action === 'reject' && 'Reject Timesheet'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">{`${selectedTimesheet.workerId?.firstName || ''} ${selectedTimesheet.workerId?.lastName || ''}`.trim() || 'Worker'}</p>
            </div>

            <div className="p-6 space-y-4">
              {/* Adjust Hours Fields */}
              {action === 'adjust' && (
                <div className="space-y-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-gray-900">Adjust Hours</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Regular Hours</label>
                      <input
                        type="number"
                        step="0.25"
                        value={adjustedHours.regular}
                        onChange={(e) => setAdjustedHours({ ...adjustedHours, regular: parseFloat(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-600 mt-1">Original: {selectedTimesheet.totals?.regularHours || 0}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Overtime Hours</label>
                      <input
                        type="number"
                        step="0.25"
                        value={adjustedHours.overtime}
                        onChange={(e) => setAdjustedHours({ ...adjustedHours, overtime: parseFloat(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-600 mt-1">Original: {selectedTimesheet.totals?.overtimeHours || 0}</p>
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border border-blue-300">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Estimated New Pay:</span> {formatCurrency(calculatePayChange())}
                    </p>
                  </div>
                </div>
              )}

              {/* Feedback/Reason Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {action === 'reject' || action === 'adjust' ? 'Reason *' : 'Notes'}
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder={
                    action === 'approve' ? 'Optional: Add any notes...' :
                    action === 'adjust' ? 'Explain why hours were adjusted...' :
                    'Explain why timesheet is being rejected...'
                  }
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {(action === 'approve' || action === 'adjust') && (
                <div className="space-y-3 bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <h3 className="font-semibold text-gray-900">Worker Performance Rating (required)</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      ['ratingOverall', 'Overall *'],
                      ['ratingAttendance', 'Attendance'],
                      ['ratingProfessionalism', 'Professionalism'],
                      ['ratingClinicalFit', 'Clinical Fit'],
                    ].map(([key, label]) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                        <input
                          type="number"
                          min="0"
                          max="5"
                          step="1"
                          value={workerRating[key]}
                          onChange={(e) =>
                            setWorkerRating((prev) => ({
                              ...prev,
                              [key]: Number(e.target.value || 0),
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Rating comments</label>
                    <textarea
                      value={workerRating.comments}
                      onChange={(e) => setWorkerRating((prev) => ({ ...prev, comments: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="Optional comments about performance, attendance, or fit."
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={action === 'approve' ? handleApprove : action === 'adjust' ? handleAdjust : handleReject}
                disabled={submitting}
                className={`px-6 py-2 text-white rounded-lg font-medium ${
                  action === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                  action === 'adjust' ? 'bg-blue-600 hover:bg-blue-700' :
                  'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50`}
              >
                {submitting ? 'Processing...' : (
                  action === 'approve' ? 'Approve' :
                  action === 'adjust' ? 'Adjust & Approve' :
                  'Reject'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
